import React, { useEffect, useState, useCallback } from 'react';
import { Search, ChevronDown, ChevronUp, Globe, Phone, Mail, Calendar, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, LeadGenLead } from '../services/api';
import { useClient } from '../contexts/ClientContext';

const STATUS_OPTIONS = ['new', 'contacted', 'qualified', 'quoted', 'won', 'lost'];

const statusColors: Record<string, string> = {
  new: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  contacted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  qualified: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  quoted: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  won: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
};

const LeadGenLeads: React.FC = () => {
  const { selectedPropertyId, leadgenProperties } = useClient();
  const [leads, setLeads] = useState<LeadGenLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState(selectedPropertyId || '');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, page_size: 25 };
      if (propertyFilter) params.property = propertyFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const result = await api.getLeadGenLeads(params as any);
      setLeads(result.results);
      setTotalCount(result.count);
      setTotalPages(result.total_pages);
    } catch (err) {
      console.error('Failed to load leads', err);
    } finally {
      setLoading(false);
    }
  }, [page, propertyFilter, statusFilter, search]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  useEffect(() => {
    setPropertyFilter(selectedPropertyId || '');
    setPage(1);
  }, [selectedPropertyId]);

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    try {
      await api.updateLeadGenLeadStatus(leadId, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadLeads();
  };

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/leadgen" className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Leads</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">{totalCount} total leads</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
        <form onSubmit={handleSearch} className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search name, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onBlur={() => { setPage(1); loadLeads(); }}
            className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none"
          />
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 text-gray-700 dark:text-gray-300"
        >
          <option value="">All Statuses</option>
          {STATUS_OPTIONS.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select
          value={propertyFilter}
          onChange={(e) => { setPropertyFilter(e.target.value); setPage(1); }}
          className="text-sm bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded px-2 py-1.5 text-gray-700 dark:text-gray-300"
        >
          <option value="">All Properties</option>
          {leadgenProperties.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-3 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            No leads found. {search || statusFilter ? 'Try adjusting your filters.' : 'Leads will appear here as they come in.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Name</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Contact</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Service</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Property</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Status</th>
                <th className="text-left py-3 px-4 text-gray-500 dark:text-gray-400 font-medium">Date</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {leads.map((lead) => (
                <React.Fragment key={lead.id}>
                  <tr
                    className="border-b border-gray-100 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                  >
                    <td className="py-3 px-4 text-gray-900 dark:text-white font-medium">{lead.name}</td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-0.5">
                        {lead.phone && <span className="text-gray-700 dark:text-gray-300">{lead.phone}</span>}
                        {lead.email && <span className="text-gray-500 dark:text-gray-400 text-xs">{lead.email}</span>}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700 dark:text-gray-300">{lead.service_requested || '—'}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <Globe className="w-3.5 h-3.5 text-gray-400" />
                        <span className="text-gray-600 dark:text-gray-400">{lead.property_name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <select
                        value={lead.status}
                        onChange={(e) => { e.stopPropagation(); handleStatusChange(lead.id, e.target.value); }}
                        onClick={(e) => e.stopPropagation()}
                        className={`text-xs rounded-full px-2 py-1 font-medium border-0 cursor-pointer ${statusColors[lead.status] || 'bg-gray-100 text-gray-800'}`}
                      >
                        {STATUS_OPTIONS.map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 px-4 text-gray-500 dark:text-gray-400">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      {expandedId === lead.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </td>
                  </tr>
                  {expandedId === lead.id && (
                    <tr className="bg-gray-50 dark:bg-gray-800/50">
                      <td colSpan={7} className="px-4 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Contact Details</h4>
                            <div className="space-y-1.5">
                              {lead.phone && (
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                  <Phone className="w-3.5 h-3.5" />
                                  <a href={`tel:${lead.phone}`} className="hover:text-primary-500">{lead.phone}</a>
                                </div>
                              )}
                              {lead.email && (
                                <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                  <Mail className="w-3.5 h-3.5" />
                                  <a href={`mailto:${lead.email}`} className="hover:text-primary-500">{lead.email}</a>
                                </div>
                              )}
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(lead.created_at).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Details</h4>
                            <div className="space-y-1.5 text-gray-600 dark:text-gray-400">
                              <p><span className="text-gray-500">Source:</span> {lead.source}</p>
                              <p><span className="text-gray-500">Property:</span> {lead.property_name} ({lead.property_vertical})</p>
                              <p><span className="text-gray-500">Domain:</span> {lead.property_domain}</p>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Message</h4>
                            <p className="text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                              {lead.message || 'No message provided'}
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Page {page} of {totalPages} ({totalCount} leads)
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LeadGenLeads;
